<template>
    <div class="relative z-10 mx-auto w-full max-w-screen-xl px-4">
        <!-- Star clusters (top-left and top-right, at the tabs level) -->
        <WrGenicUiStarCluster class="top-4 -left-44 scale-50" />
        <WrGenicUiStarCluster class="bottom-60 -right-44 scale-50" />
        <!-- Heading -->
        <div class="text-center mb-10 lg:mb-14">
            <h2 class="text-3xl lg:text-[48px] leading-tight">
                <WrGradientText :text="heading" append-class="p-0" />
                <span class="block text-[#1E1B4B] dark:text-white">{{ headingSuffix }}</span>
            </h2>
            <p class="mx-auto mt-4 max-w-2xl text-base lg:text-lg leading-relaxed text-[#5A5478] dark:text-[#CBCBCB]">
                {{ description }}
            </p>
        </div>

        <!-- Industry tabs -->
        <Tabs :value="activeTab" @update:value="activeTab = $event">
            <div class="md:flex md:items-start md:gap-6 lg:gap-8">
                <!-- Left: industry tabs (desktop = vertical stack, mobile = carousel) -->
                <div class="md:w-[300px] lg:w-[340px] md:flex-shrink-0">
                    <!-- Desktop: vertical tab stack -->
                    <div class="hidden md:block">
                        <TabList unstyled :pt="{
                            tabList: { class: 'flex flex-col gap-4' },
                            activeBar: { class: 'hidden' },
                        }">
                            <Tab unstyled v-for="(tab, index) in tabs" :key="tab.title" :value="index.toString()"
                                class="transition-opacity duration-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#7782FF]"
                                :class="activeTab === index.toString() ? 'opacity-100' : 'opacity-50'">
                                <WrGenicApplicationCard :tab="tab" :active="activeTab === index.toString()" />
                            </Tab>
                        </TabList>
                    </div>

                    <!-- Mobile: swipeable carousel — the active card is the active tab (manual only, no loop) -->
                    <div class="md:hidden genic-app-carousel">
                        <Carousel :value="tabs" :numVisible="1" :numScroll="1" :page="Number(activeTab)"
                            @update:page="activeTab = String($event)" :showNavigators="false" :showIndicators="true"
                            :pt="{
                                itemList: { class: '' },
                                item: { class: 'px-2 !flex-[1_0_100%]' },
                                indicatorList: { class: '!mt-5 !p-0 !gap-2' },
                                indicatorbutton: {
                                    class: '!rounded-full !size-2.5 !border-0 !bg-[#4753DC]/30',
                                },
                            }">
                            <template #item="slotProps">
                                <div class="h-full transition-opacity duration-300"
                                    :class="slotProps.index === Number(activeTab) ? 'opacity-100' : 'opacity-50'">
                                    <WrGenicApplicationCard :tab="slotProps.data"
                                        :active="slotProps.index === Number(activeTab)" />
                                </div>
                            </template>
                        </Carousel>
                    </div>
                </div>

                <!-- Right: live playground -->
                <div class="mt-6 w-full md:mt-0 md:min-w-0 md:flex-1">
                    <TabPanels unstyled class="w-full">
                        <TabPanel unstyled v-for="(tab, index) in tabs" :key="tab.title" :value="index.toString()">
                            <WrGenicLiveDemo :demo="tab.demo"
                                :active="activeTab === index.toString()" />
                        </TabPanel>
                    </TabPanels>
                </div>
            </div>
        </Tabs>

        <WrButton class="mx-auto" container-class="mt-12" append-class="bg-[linear-gradient(95.99deg,#4753DC_3.69%,#262B7E_96.65%)]"
            @click="$emit('cta-clicked')">
            {{ ctaText }}
        </WrButton>
    </div>
</template>

<script setup>
import WrGenicLiveDemo from "./WrGenicLiveDemo.vue";
import WrGenicApplicationCard from "./WrGenicApplicationCard.vue";
import WrGenicEnterpriseIcon from "./WrGenicEnterpriseIcon.vue";
import WrGenicFintechIcon from "./WrGenicFintechIcon.vue";
import WrGenicSupplyIcon from "./WrGenicSupplyIcon.vue";
import WrGenicUiStarCluster from "./WrGenicUiStarCluster.vue";

defineEmits(["cta-clicked"]);

defineProps({
    heading: {
        type: String,
        default: "Real-World Applications",
    },
    headingSuffix: {
        type: String,
        default: "Across Industries",
    },
    description: {
        type: String,
        default:
            "Empower every sector with real-time, interactive components that seamlessly connect AI to your design system.",
    },
    ctaText: {
        type: String,
        default: "Experience GenicUI",
    },
    tabs: {
        type: Array,
        default: () => [
            {
                title: "Enterprise SaaS & B2B Software",
                text: "Native software panels, grant, user management, and onboarding flows.",
                icon: WrGenicEnterpriseIcon,
                demo: {
                    userMessage: "Add 2 team leads to the Admin role.",
                    agentMessage: "Role matrix prepared. 2 users updated for approval.",
                    chip: "#alpha-team-workshop",
                    panelTitle: "Workspace Access Control",
                    panelPill: "2 USERS TARGETED",
                    panel: {
                        type: "table",
                        head: ["Member", "Role", "Status"],
                        rows: [
                            {
                                name: "Sarah Chen",
                                role: "Lead Architect",
                                tag: "Admin",
                                initials: "SC",
                                color: "linear-gradient(135deg, #4b4fd6, #262b7e)",
                                status: "stable",
                                statusLabel: "Stable",
                            },
                            {
                                name: "Alex Rivera",
                                role: "Principal Eng",
                                tag: "Admin",
                                initials: "AR",
                                color: "linear-gradient(135deg, #8b5cf6, #5b21b6)",
                                status: "audit",
                                statusLabel: "Audit",
                            },
                        ],
                    },
                    modulesLabel: "Permission Modules",
                    modules: [
                        { name: "Billing Access", hint: "Granted", on: true },
                        { name: "API Keys", hint: "Write", on: true },
                    ],
                    applyText: "All 2 users confirmed pending attestation.",
                    applyLabel: "Apply Changes",
                },
            },
            {
                title: "Fintech & Financial Services",
                text: "Live portfolio views, loan portals, risk dashboards, and compliance forms.",
                icon: WrGenicFintechIcon,
                demo: {
                    userMessage: "Show my portfolio performance for Q3.",
                    agentMessage: "Here's your Q3 portfolio performance.",
                    chip: "#q3-portfolio",
                    panelTitle: "Portfolio Performance",
                    panelPill: "Q3 2026",
                    panel: {
                        type: "chart",
                        axis: ["Jul", "Aug", "Sep"],
                    },
                },
            },
            {
                title: "Supply Chain & Logistics",
                text: "Real-time fleet tracking, smart inventory dashboards, and allocation portals.",
                icon: WrGenicSupplyIcon,
                demo: {
                    userMessage: "Reallocate 120 units from Hub West to Hub East.",
                    agentMessage: "Rebalance planned. Route is live and tracking in real time.",
                    chip: "#hub-east-rebalance",
                    panelTitle: "Rebalance Route",
                    panelPill: "ETA 6:40 PM",
                    panel: {
                        type: "route",
                        stops: [
                            { name: "Hub West", time: "9:10 AM", state: "done" },
                            { name: "Cross-dock", time: "1:35 PM", state: "active" },
                            { name: "Hub East", time: "6:40 PM", state: "upcoming" },
                        ],
                        meta: "120 units · 2 vehicles · Lane 4",
                    },
                    applyText: "120 units staged for dispatch.",
                    applyLabel: "Dispatch",
                },
            },
        ],
    },
});

const Tabs = defineAsyncComponent(() => import("primevue/tabs"));
const TabList = defineAsyncComponent(() => import("primevue/tablist"));
const Tab = defineAsyncComponent(() => import("primevue/tab"));
const TabPanels = defineAsyncComponent(() => import("primevue/tabpanels"));
const TabPanel = defineAsyncComponent(() => import("primevue/tabpanel"));
const Carousel = defineAsyncComponent(() => import("primevue/carousel"));

const activeTab = ref("0");
</script>

<style>

.genic-app-carousel .p-carousel-indicator-active .p-carousel-indicator-button {
    background-color: #4753DC !important;
}

.dark .genic-app-carousel .p-carousel-indicator-active .p-carousel-indicator-button {
    background-image: linear-gradient(95.69deg, #8F8AF2 2.69%, #3838A6 113.37%) !important;
}
</style>
